import { Component, effect, inject, signal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { map } from 'rxjs';
import type { BusinessUnitManagementModel } from '../../../models';
import { ManagementService } from '../../../services/management.service';
import { BusinessUnitDetailViewComponent } from '../business-unit-detail-view.component/business-unit-detail-view.component';
import { BusinessUnitSidebarComponent } from '../business-unit-sidebar/business-unit-sidebar.component';
import { CreateBusinessUnitDialogComponent } from '../dialogs/create-business-unit-dialog/create-business-unit-dialog.component';

@Component({
  selector: 'dtis-admin-business-unit-management',
  host: { class: 'flex h-full w-full' },
  imports: [BusinessUnitSidebarComponent, BusinessUnitDetailViewComponent, MatProgressSpinnerModule, TranslocoModule],
  templateUrl: './business-unit-management.component.html',
  styles: [
    `
      @reference "tailwindcss";
      :host {
        @apply flex h-full w-full overflow-hidden;
      }
    `,
  ],
})
/**
 * BusinessUnitManagementComponent
 *
 * Main page component for managing business units with sidebar navigation
 * and detailed view with tabs.
 *
 * Features:
 * - Sidebar navigation with search/filter
 * - Detailed view with Overview, Users, and Administrators tabs
 * - Explicit edit mode with save/cancel actions
 * - Statistics badges and visual feedback
 *
 * @example
 * <dtis-admin-business-unit-management></dtis-admin-business-unit-management>
 */
export class BusinessUnitManagementComponent {
  private readonly managementService = inject(ManagementService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly transloco = inject(TranslocoService);

  /**
   * The list of business units.
   */
  businessUnitsList = rxResource({
    stream: () => this.managementService.getBusinessUnits(),
  });

  /**
   * The currently selected business unit.
   */
  selectedUnit = signal<BusinessUnitManagementModel | undefined>(undefined);

  /**
   * Current route parameter ID as a signal
   */
  private readonly routeParamId = toSignal(
    // biome-ignore lint/complexity/useLiteralKeys: This is a common pattern for extracting route parameters, and the complexity is justified.
    this.route.params.pipe(map((params) => params['id']))
  );

  constructor() {
    // Effect to handle route parameter changes and initial load
    effect(() => {
      const units = this.businessUnitsList.error() ? undefined : this.businessUnitsList.value();
      const id = this.routeParamId();

      if (!units || !id) {
        this.selectedUnit.set(undefined);
        return;
      }

      const unit = units.find((u) => u.identifier === id);
      if (unit) {
        this.selectedUnit.set(unit);
      } else {
        // ID in route but unit not found, navigate to base route
        this.router.navigate(['/business-unit-management']);
      }
    });
  }

  /**
   * Selects a business unit to view/edit.
   * @param unit The business unit to select
   */
  selectBusinessUnit(unit: BusinessUnitManagementModel): void {
    this.selectedUnit.set(unit);
    this.router.navigate(['/business-unit-management', unit.identifier]);
  }

  onLeaveBusinessUnit(): void {
    this.selectedUnit.set(undefined);
    this.businessUnitsList.reload();
    this.router.navigate(['/business-unit-management']).then(
      (navigated) => {
        if (!navigated) {
          this.showLeaveNavigationError();
        }
      },
      (error: unknown) => {
        console.error('Navigation after leaving business unit failed:', error);
        this.showLeaveNavigationError();
      }
    );
  }

  private showLeaveNavigationError(): void {
    this.snackBar.open(this.transloco.translate('membership-navigation-failed'), undefined, {
      duration: 5000,
    });
  }

  /**
   * Opens the create business unit modal dialog.
   */
  openCreateBusinessUnitDialog(): void {
    const dialogRef = this.dialog.open(CreateBusinessUnitDialogComponent, {
      disableClose: true,
      minWidth: '500px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Refresh the business units list after successful creation
        this.businessUnitsList.reload();
        this.snackBar.open('Business unit created successfully', 'Close', {
          duration: 3000,
        });
      }
    });
  }

  /**
   * Deletes a business unit.
   * @param identifier The identifier of the business unit to delete
   */
  deleteBusinessUnit(_identifier: string): void {
    this.snackBar.open('Delete functionality coming soon', 'Close', {
      duration: 3000,
    });
  }
}
